import { Box, ImageList, ImageListItem, Link, Stack, Typography } from '@mui/material';
import type { ApprovedContentItem, Citation } from '@dvgup/contracts/generated/approved-content-api';

/**
 * Forward-compat shape for content items rendered on the Daily page.
 *
 * The current generated `ApprovedContentItem` (per
 * `models/src/generated/approved-content-api/models_0.ts`) carries
 * `id`, `type`, `date`, `body`, `citations`, `affiliateLinks`, and
 * `metadata` — but not `title`, `primaryImage`, `gallery`, or
 * `platforms`. Those fields land on the per-item record once the
 * `smithy-api-layer` follow-up A regenerates the IDL. Until then,
 * the SPA's renderer treats them as optional and falls back to
 * `headline` / `image` (the seed-record field names today) so
 * placeholder content surfaces.
 */
export type RenderableContentItem = ApprovedContentItem & {
  readonly title?: string;
  readonly headline?: string;
  readonly game_title?: string;
  readonly primaryImage?: string;
  readonly image?: string;
  readonly gallery?: ReadonlyArray<string>;
  readonly platforms?: ReadonlyArray<string>;
  readonly source?: string;
  readonly source_url?: string;
};

/** Resolve the title from `title`, `headline`, `game_title`, or empty. */
export function pickTitle(item: RenderableContentItem): string {
  return item.title ?? item.headline ?? item.game_title ?? '';
}

/** Resolve the primary image from `primaryImage` or the seed `image` field. */
export function pickPrimaryImage(item: RenderableContentItem): string | undefined {
  return item.primaryImage ?? item.image;
}

/** Render a comma-separated list of citation links (sources). */
function CitationsLine({ citations }: { readonly citations?: ReadonlyArray<Citation> }): JSX.Element | null {
  if (!citations || citations.length === 0) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
      {citations.map((citation, index) => {
        const label = citation.source ?? 'source';
        const href = citation.url;
        return (
          <Box component="span" key={`${label}-${index}`}>
            {index > 0 && ' \u00B7 '}
            {href ? (
              <Link href={href} rel="noopener noreferrer" target="_blank" color="inherit">
                {label}
              </Link>
            ) : (
              <Box component="span">{label}</Box>
            )}
          </Box>
        );
      })}
    </Typography>
  );
}

interface ContentCardProps {
  readonly item: RenderableContentItem;
  /** Heading element type — `h2` for the fact-of-the-day, `h3` (default) for news. */
  readonly headingComponent?: 'h2' | 'h3';
  /** When true, suppresses the gallery row even if `gallery` is set. */
  readonly suppressGallery?: boolean;
}

/**
 * `<ContentCard>` — renders a single news / fact item inside the
 * Daily page's news + fact-of-the-day sections per Requirement 3.1.8
 * and 3.1.10.
 *
 * Renders **without** MUI `<Card>` chrome — the Daily page presents
 * news and fact-of-the-day as continuous flowing content, not as
 * boxed cards. The component composes a heading, optional primary
 * image, optional gallery row, the body copy, and a citations line.
 */
export function ContentCard({
  item,
  headingComponent = 'h3',
  suppressGallery = false,
}: ContentCardProps): JSX.Element {
  const title = pickTitle(item);
  const primaryImage = pickPrimaryImage(item);
  const gallery = !suppressGallery && item.gallery && item.gallery.length > 0 ? item.gallery : undefined;
  const headingVariant = headingComponent === 'h2' ? 'h2' : 'h3';
  return (
    <Stack spacing={1.5}>
      {title && (
        <Typography variant={headingVariant} component={headingComponent}>
          {title}
        </Typography>
      )}
      {primaryImage && (
        <Box
          component="img"
          src={primaryImage}
          alt={title || 'Image'}
          sx={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 1 }}
        />
      )}
      {gallery && (
        <ImageList cols={Math.min(gallery.length, 3)} gap={8} rowHeight={160}>
          {gallery.map((src, index) => (
            <ImageListItem key={`${src}-${index}`}>
              <Box
                component="img"
                src={src}
                alt={`${title || 'Gallery'} image ${index + 1}`}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}
      {item.body && <Typography variant="body1">{item.body}</Typography>}
      <CitationsLine citations={item.citations as ReadonlyArray<Citation> | undefined} />
    </Stack>
  );
}
